import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export const cn = (...inputs: ClassValue[]): string => twMerge(clsx(inputs));

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export const formatDate = (value: string | null | undefined): string => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

export const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '—';
  return new Date(value).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** "3 min ago" / "in 2 h" — the form that actually helps when watching a queue. */
export const formatRelative = (value: string | null | undefined): string => {
  if (!value) return '—';
  const target = new Date(value).getTime();
  const deltaSeconds = Math.round((target - Date.now()) / 1000);
  const absolute = Math.abs(deltaSeconds);

  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['second', 60],
    ['minute', 60],
    ['hour', 24],
    ['day', 7],
    ['week', 4.35],
    ['month', 12],
    ['year', Number.POSITIVE_INFINITY],
  ];

  let amount = deltaSeconds;
  for (const [unit, step] of units) {
    if (Math.abs(amount) < step || unit === 'year') {
      return new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' }).format(
        Math.round(amount),
        unit,
      );
    }
    amount /= step;
  }
  return absolute < 60 ? 'just now' : formatDateTime(value);
};

export const formatNumber = (value: number | null | undefined): string =>
  value === null || value === undefined ? '—' : new Intl.NumberFormat().format(value);

export const formatPercent = (value: number | null | undefined, digits = 1): string =>
  value === null || value === undefined
    ? '—'
    : `${(value * 100).toFixed(digits).replace(/\.0$/, '')}%`;

export const initials = (value: string): string =>
  value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || '?';

export const truncate = (value: string, max: number): string =>
  value.length <= max ? value : `${value.slice(0, max - 1)}…`;

export const parseTags = (value: string | null | undefined): string[] =>
  (value ?? '')
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);

/** Human label for a SCREAMING_SNAKE status. */
export const titleCase = (value: string): string =>
  value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const download = (filename: string, content: string, mime = 'text/csv'): void => {
  const blob = new Blob([content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

export const copyToClipboard = async (value: string): Promise<void> => {
  await navigator.clipboard.writeText(value);
};

/** Deterministic pleasant colour for a string, used for lead-type dots and avatars. */
export const colorFor = (value: string): string => {
  const palette = ['#2F52EB', '#7C4DFF', '#0E9F8E', '#E08700', '#E0365E', '#0891B2', '#C026D3'];
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) % 100000;
  }
  return palette[hash % palette.length];
};
